/**
 * Commerce · Pricing Engine (Wave 1).
 * 9 rule kinds + priority/margin/effective-date/approval.
 * Uses RecordEditor for creation; Toggle for activation. Reuses existing primitives only.
 */
import { useMemo, useState } from "react";
import { Tag, Users, MapPin, Zap, Layers, Store, Globe, Sun, Award, Building2, User } from "lucide-react";
import { Card, SectionHeader, Button, TextInput } from "../../components/lib";
import { TabBar, Field, Toggle } from "../../components/lib.overlay";
import { RecordEditor, FieldSpec } from "../../components/RecordEditor";
import { PRICE_RULES, PriceRule, PriceRuleKind } from "../../mocks/commerceData";

const KIND_META: Record<PriceRuleKind, { icon: any; label: string; tint: string; fg: string }> = {
  retail:             { icon: Tag,      label: "Retail",       tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  wholesale:          { icon: Building2,label: "Wholesale",    tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  distributor:        { icon: Store,    label: "Distributor",  tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  vip:                { icon: Award,    label: "VIP",          tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  staff:              { icon: User,     label: "Staff",        tint: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)"      },
  marketplace:        { icon: Layers,   label: "Marketplace",  tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  export:             { icon: Globe,    label: "Export",       tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  seasonal:           { icon: Sun,      label: "Seasonal",     tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  customer_specific:  { icon: Users,    label: "Customer",     tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  // legacy kinds retained for back-compat
  tiered:             { icon: Layers,   label: "Tiered",       tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  member:             { icon: Users,    label: "Member",       tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  geo:                { icon: MapPin,   label: "Geo",          tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  flash:              { icon: Zap,      label: "Flash",        tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
};

const RULE_SCHEMA: FieldSpec[] = [
  { key: "name",          label: "Rule name",       kind: "text",     placeholder: "VIP · Q1 2026" },
  { key: "kind",          label: "Kind",            kind: "select", options: [
      { value: "retail",             label: "Retail" },
      { value: "wholesale",          label: "Wholesale" },
      { value: "distributor",        label: "Distributor" },
      { value: "vip",                label: "VIP" },
      { value: "staff",              label: "Staff" },
      { value: "marketplace",        label: "Marketplace" },
      { value: "export",             label: "Export" },
      { value: "seasonal",           label: "Seasonal" },
      { value: "customer_specific",  label: "Customer-specific" },
  ]},
  { key: "scope",         label: "Scope",           kind: "select", options: [
      { value: "all",    label: "All products" },
      { value: "family", label: "Family" },
      { value: "sku",    label: "Specific SKU" },
  ]},
  { key: "target",        label: "Target",          kind: "text",   placeholder: "ORV-NA-050 or family" },
  { key: "detail",        label: "Detail",          kind: "text",   placeholder: "-15% · min 3 units" },
  { key: "marginPct",     label: "Margin %",        kind: "number", placeholder: "45" },
  { key: "priority",      label: "Priority",        kind: "number", placeholder: "10" },
  { key: "activeFrom",    label: "Start",           kind: "text",   placeholder: "2026-02-01" },
  { key: "activeTo",      label: "End",             kind: "text",   placeholder: "2026-03-31" },
  { key: "effectiveDate", label: "Effective date",  kind: "text",   placeholder: "2026-02-05" },
  { key: "approvalStatus",label: "Approval",        kind: "select", options: [
      { value: "draft",    label: "Draft" },
      { value: "pending",  label: "Pending" },
      { value: "approved", label: "Approved" },
      { value: "rejected", label: "Rejected" },
  ]},
  { key: "active",        label: "Active",          kind: "toggle" },
];

export default function Pricing() {
  const [rules, setRules] = useState<PriceRule[]>(PRICE_RULES);
  const [tab, setTab] = useState<PriceRuleKind | "all">("all");
  const [q, setQ] = useState("");
  const [editorOpen, setEditorOpen] = useState<"create" | "edit" | null>(null);
  const [selected, setSelected] = useState<PriceRule | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rules.length };
    rules.forEach((r) => { c[r.kind] = (c[r.kind] || 0) + 1; });
    return c;
  }, [rules]);

  const filtered = useMemo(
    () => rules.filter((r) =>
      (tab === "all" || r.kind === tab) &&
      (!q || r.name.toLowerCase().includes(q.toLowerCase()) || r.target.toLowerCase().includes(q.toLowerCase())),
    ),
    [rules, tab, q],
  );

  const toggle = (id: string) => setRules((list) => list.map((r) => r.id === id ? { ...r, active: !r.active } : r));

  return (
    <div className="space-y-6" data-testid="cm-pricing">
      <Card testId="cm-pricing-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search rules…" testId="cm-pricing-search" /></div>
          <Button testId="cm-pricing-add" onClick={() => { setSelected(null); setEditorOpen("create"); }}>+ New rule</Button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <TabBar<PriceRuleKind | "all">
            testId="cm-pricing-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",               label: "All",         count: counts.all         },
              { key: "retail",            label: "Retail",      count: counts.retail      || 0 },
              { key: "wholesale",         label: "Wholesale",   count: counts.wholesale   || 0 },
              { key: "distributor",       label: "Distributor", count: counts.distributor || 0 },
              { key: "vip",               label: "VIP",         count: counts.vip         || 0 },
              { key: "staff",             label: "Staff",       count: counts.staff       || 0 },
              { key: "marketplace",       label: "Marketplace", count: counts.marketplace || 0 },
              { key: "export",            label: "Export",      count: counts.export      || 0 },
              { key: "seasonal",          label: "Seasonal",    count: counts.seasonal    || 0 },
              { key: "customer_specific", label: "Customer",    count: counts.customer_specific || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="cm-pricing-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Rules" title={`${filtered.length} pricing rule${filtered.length === 1 ? "" : "s"}`} action={<Tag size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        </div>
        <div className="p-2" data-testid="cm-pricing-list">
          {filtered.map((r) => {
            const meta = KIND_META[r.kind] || KIND_META.retail;
            const Icon = meta.icon;
            const approval = r.approvalStatus || "approved";
            const approvalTint = approval === "approved" ? "var(--ph-success)" : approval === "rejected" ? "var(--ph-danger)" : "var(--ph-warning)";
            return (
              <div
                key={r.id}
                data-testid={`cm-price-${r.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[var(--ph-surface-hover)]"
                style={{ borderRadius: "var(--ph-radius-sm)" }}
                onClick={() => { setSelected(r); setEditorOpen("edit"); }}
              >
                <span className="inline-flex items-center justify-center flex-shrink-0"
                  style={{ width: 28, height: 28, borderRadius: "999px", background: meta.tint, color: meta.fg }}>
                  <Icon size={13} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{r.name}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {meta.label}
                    </span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", color: approvalTint, border: `1px solid ${approvalTint}`, borderRadius: "var(--ph-radius-sm)" }}>
                      {approval}
                    </span>
                    {typeof r.priority === "number" && (
                      <span className="text-[10px] tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>P{r.priority}</span>
                    )}
                    {typeof r.marginPct === "number" && (
                      <span className="text-[10px] tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>margin {r.marginPct}%</span>
                    )}
                  </div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: "var(--ph-ink-muted)" }}>
                    {r.target} · {r.detail} · {r.activeFrom} → {r.activeTo}{r.effectiveDate ? ` · effective ${r.effectiveDate}` : ""}
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Toggle
                    checked={r.active}
                    onChange={() => toggle(r.id)}
                    testId={`cm-price-toggle-${r.id}`}
                    label={`Toggle ${r.name}`}
                  />
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-sm text-center py-8" style={{ color: "var(--ph-ink-muted)" }}>No rules match your filter.</div>
          )}
        </div>
      </Card>

      <Card testId="cm-pricing-preview">
        <SectionHeader eyebrow="Preview" title="Applied to first live SKU" />
        <div className="text-sm py-4 text-center" style={{ color: "var(--ph-ink-muted)" }} data-testid="cm-pricing-preview-empty">
          No live SKUs yet. The preview shows list price, loyalty tiers, and wholesale rates once you have a product in the master.
        </div>
      </Card>

      <RecordEditor<PriceRule>
        open={editorOpen !== null}
        onClose={() => setEditorOpen(null)}
        mode={editorOpen === "edit" ? "edit" : "create"}
        title={editorOpen === "edit" ? "Edit pricing rule" : "New pricing rule"}
        collection="price_rules"
        schema={RULE_SCHEMA}
        initial={editorOpen === "edit" && selected ? (selected as unknown as Record<string, unknown>) : { kind: "retail", scope: "sku", active: true, approvalStatus: "draft" }}
        onSaved={(row) => {
          const saved = row as unknown as PriceRule;
          setRules((list) => {
            const idx = list.findIndex((r) => r.id === saved.id);
            if (idx >= 0) { const copy = [...list]; copy[idx] = saved; return copy; }
            return [saved, ...list];
          });
        }}
        testId="cm-price-editor"
      />
    </div>
  );
}
